import { useTranslation } from 'react-i18next';
import { Icon24Close } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { AppDialog, AppDialogActions, AppDialogContent, AppDialogSection } from '@/UI/AppDialog/AppDialog';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { FlexCol } from '@/UI/styles';
import { Fade } from '@/UI/Transitions/Fade';
import { I18N_KEYS } from '@/language/keys';
import { useWithLoader } from '@/hooks/useWithLoader';
import { useTour } from '../TourContext';
import { TourActionButton } from './TourModal';
import type { TourAction } from '../tourTypes';

const MODAL_WIDTH = 400;
const MEDIA_HEIGHT = 227;
const INTRO_MEDIA = `${import.meta.env.BASE_URL}tour-assets/tool-builder/intro.jpg`;

interface ToolBuilderIntroModalProps {
  actions?: TourAction[];
  content: string;
  onClose?: () => void;
  open: boolean;
  title: string;
}

function ToolBuilderIntroMedia({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="tool-builder-intro-media"
      sx={{
        backgroundColor: cssVars.color.bgSecondary,
        height: MEDIA_HEIGHT,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      <Box
        alt=""
        component="img"
        src={INTRO_MEDIA}
        sx={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }}
      />
      <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
        <AppIconButton aria-label={t(I18N_KEYS.GENERAL.CLOSE)} height={24} onClick={onClose} width={24}>
          <Icon24Close aria-hidden />
        </AppIconButton>
      </Box>
    </Box>
  );
}

/** FPL-aligned first-time introduction shown when an editor first enters Tool mode. */
export function ToolBuilderIntroModal({ actions, content, onClose, open, title }: ToolBuilderIntroModalProps) {
  const { t } = useTranslation();
  const { callAction } = useTour();
  const { isLoading, execute: runAction } = useWithLoader(callAction, { sync: true });

  return (
    <AppDialog
      aria-label={t(title)}
      open={open}
      width={MODAL_WIDTH}
      slots={{ transition: Fade }}
      slotProps={{
        paper: { sx: { background: cssVars.color.bg } },
        transition: { timeout: 0 },
      }}
    >
      <AppDialogSection>
        <AppDialogContent sx={{ p: 0 }}>
          <ToolBuilderIntroMedia onClose={onClose} />
          <FlexCol
            data-testid="tool-builder-intro-text"
            sx={{
              gap: 0.5,
              px: 2,
              py: 2,
            }}
          >
            {/* `body-sm-md` maps to FPL's regular weight; the design's title is strong. */}
            <TextStdMd color={cssVars.color.text}>{t(title)}</TextStdMd>
            <TextSmRg color={cssVars.color.text}>{t(content)}</TextSmRg>
          </FlexCol>
          {/* The design puts this rule on Modal.Footer, but FPL's CustomFooter draws no border and
              AppDialogActions drops sx on the FPL path, so the body owns it. */}
          <Box
            data-testid="tool-builder-intro-divider"
            sx={{ backgroundColor: cssVars.color.border, height: '1px', width: '100%' }}
          />
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'space-between' }}>
          {actions?.map((action) => (
            // The footer pads 16 on the left and a text button pads its label again, so pull the
            // secondary action back to line its label up with the body text above.
            <Box key={action.label} sx={action.variant === 'secondary' ? { ml: -1 } : undefined}>
              <TourActionButton action={action} isLoading={isLoading} onClick={() => runAction(action)} />
            </Box>
          ))}
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}
